import { trackUsage } from "@/lib/usage";
import type { BoardSummary } from "@mn/shared";
import {
  bringToFront,
  type CardFields,
  type CardType,
  cardFields,
  cardPreview,
  createCard,
  createLine,
  deserializeCard,
  ensureSynced,
  jsonToFragment,
  LOCAL,
  pruneLines,
  sendToBack,
  serializeCard,
} from "@mn/shared";
import * as Y from "yjs";
import { api } from "@/lib/api";
import type { BoardHandle } from "./collab";
import { useBoardUI } from "./store";

/** Board-level commands used by the toolbar, context menu and shortcuts. */
export class Actions {
  /** Spatial index of the board (set by BoardPage); used by align/distribute for measured heights. */
  index: { rectOf: (id: string) => { x: number; y: number; w: number; h: number } | null } | null = null;

  constructor(
    private handle: BoardHandle,
    private boardId: string,
    private userId: string,
  ) {}

  get doc() {
    return this.handle.doc;
  }
  get cards() {
    return this.handle.types.cards;
  }

  async create(type: CardType, x: number, y: number, fields?: Partial<CardFields>): Promise<string> {
    let size: { w?: number; h?: number | null } = {};
    if (type === "annotation") {
      // A drawing layer covers the current view; it sits behind nothing and ignores the pointer unless selected.
      const { camera, viewport } = useBoardUI.getState();
      x = -camera.x + 20;
      y = -camera.y + 20;
      size = { w: Math.round(viewport.w / camera.z) - 40, h: Math.round(viewport.h / camera.z) - 40 };
      fields = { ...fields, color: "transparent" };
    }
    if (type === "board") {
      const b = await api<BoardSummary>("/api/boards", {
        method: "POST",
        json: { parentId: this.boardId, title: "Nuevo tablero" },
      });
      fields = { ...fields, childBoardId: b.id };
    }
    const id = createCard(this.doc, { type, x, y, userId: this.userId, fields, ...size });
    this.handle.undo.stopCapturing();
    const ui = useBoardUI.getState();
    ui.select([id]);
    if (type === "note" || type === "heading" || type === "annotation") ui.setEditing(id);
    trackUsage("card_create", type);
    return id;
  }

  /** Make a note synced and place a clone next to it: both edit the same shared body. */
  cloneSynced(cardId: string) {
    const src = this.cards.get(cardId);
    if (!src) return;
    const key = (src.get("syncedKey") as string | undefined) ?? ensureSynced(this.doc, cardId);
    if (!key) return;
    const f = cardFields(src);
    const id = createCard(this.doc, {
      type: "clone",
      x: f.x + 40,
      y: f.y + 40,
      w: f.w,
      h: f.h,
      userId: this.userId,
      fields: { syncedKey: key, color: f.color, colorMode: f.colorMode } as Partial<CardFields>,
    });
    this.handle.undo.stopCapturing();
    useBoardUI.getState().select([id]);
  }

  /** Align the selected free cards along an edge or centre. Needs 2+ cards. */
  align(how: "left" | "centerX" | "right" | "top" | "centerY" | "bottom", ids = this.selectedIds()) {
    const rects = ids
      .map((id) => ({ id, r: this.index?.rectOf(id) ?? null, c: this.cards.get(id) }))
      .filter(
        (x): x is { id: string; r: { x: number; y: number; w: number; h: number }; c: Y.Map<unknown> } =>
          !!x.r && !!x.c && !x.c.get("parent") && !x.c.get("locked"),
      );
    if (rects.length < 2) return;
    const minX = Math.min(...rects.map((x) => x.r.x));
    const maxX = Math.max(...rects.map((x) => x.r.x + x.r.w));
    const minY = Math.min(...rects.map((x) => x.r.y));
    const maxY = Math.max(...rects.map((x) => x.r.y + x.r.h));
    this.doc.transact(() => {
      for (const { r, c } of rects) {
        if (how === "left") c.set("x", minX);
        else if (how === "right") c.set("x", maxX - r.w);
        else if (how === "centerX") c.set("x", Math.round((minX + maxX) / 2 - r.w / 2));
        else if (how === "top") c.set("y", minY);
        else if (how === "bottom") c.set("y", maxY - r.h);
        else if (how === "centerY") c.set("y", Math.round((minY + maxY) / 2 - r.h / 2));
      }
    }, LOCAL);
    this.handle.undo.stopCapturing();
  }

  /** Space the selected free cards evenly between the first and the last one. Needs 3+ cards. */
  distribute(axis: "x" | "y", ids = this.selectedIds()) {
    const rects = ids
      .map((id) => ({ id, r: this.index?.rectOf(id) ?? null, c: this.cards.get(id) }))
      .filter(
        (x): x is { id: string; r: { x: number; y: number; w: number; h: number }; c: Y.Map<unknown> } =>
          !!x.r && !!x.c && !x.c.get("parent") && !x.c.get("locked"),
      )
      .sort((a, b) => (axis === "x" ? a.r.x - b.r.x : a.r.y - b.r.y));
    if (rects.length < 3) return;
    const size = (r: { w: number; h: number }) => (axis === "x" ? r.w : r.h);
    const first = rects[0]!;
    const last = rects[rects.length - 1]!;
    const start = axis === "x" ? first.r.x : first.r.y;
    const end = (axis === "x" ? last.r.x : last.r.y) + size(last.r);
    const total = rects.reduce((a, x) => a + size(x.r), 0);
    const gap = (end - start - total) / (rects.length - 1);
    let cursor = start;
    this.doc.transact(() => {
      for (const { r, c } of rects) {
        c.set(axis, Math.round(cursor));
        cursor += size(r) + gap;
      }
    }, LOCAL);
    this.handle.undo.stopCapturing();
  }

  /** Open the board behind a tile or shortcut (BoardPage listens and navigates). */
  openTile(id: string) {
    const c = this.cards.get(id);
    const target = (c?.get("childBoardId") ?? c?.get("targetBoardId")) as string | undefined;
    if (target) window.dispatchEvent(new CustomEvent("mn:open-board", { detail: { boardId: target } }));
  }

  /** Copy a note as a synced reference (localStorage), to be pasted on any board as a live clone. */
  copySynced(cardId: string) {
    const src = this.cards.get(cardId);
    if (!src) return;
    const key = (src.get("syncedKey") as string | undefined) ?? ensureSynced(this.doc, cardId);
    if (!key) return;
    const f = cardFields(src);
    const snap = serializeCard(src);
    try {
      localStorage.setItem(
        "mn_synced_clip",
        JSON.stringify({
          key,
          sourceBoardId: this.boardId,
          w: f.w,
          h: f.h,
          color: f.color,
          colorMode: f.colorMode,
          body: snap.body ?? null,
          at: Date.now(),
        }),
      );
    } catch {}
  }

  /** What copySynced stored, if anything (valid for a day). */
  static syncedClip(): {
    key: string;
    sourceBoardId: string;
    w: number;
    h: number | null;
    color: string;
    colorMode: string;
    body: unknown;
    at: number;
  } | null {
    try {
      const raw = localStorage.getItem("mn_synced_clip");
      if (!raw) return null;
      const c = JSON.parse(raw);
      return Date.now() - (c.at ?? 0) < 86_400_000 ? c : null;
    } catch {
      return null;
    }
  }

  /** Paste the copied synced note here: same key, body seeded from the copy, link registered so the server relays edits. */
  async pasteSynced(x: number, y: number) {
    const clip = Actions.syncedClip();
    if (!clip) return;
    const { synced } = this.handle.types;
    if (!synced.has(clip.key)) {
      this.doc.transact(() => {
        const frag = new Y.XmlFragment();
        synced.set(clip.key, frag);
        if (clip.body) jsonToFragment(clip.body, frag);
      }, LOCAL);
    }
    const id = createCard(this.doc, {
      type: "clone",
      x,
      y,
      w: clip.w,
      h: clip.h,
      userId: this.userId,
      fields: {
        syncedKey: clip.key,
        color: clip.color,
        colorMode: clip.colorMode as "background" | "strip",
      } as Partial<CardFields>,
    });
    this.handle.undo.stopCapturing();
    useBoardUI.getState().select([id]);
    if (clip.sourceBoardId !== this.boardId)
      await api(`/api/boards/${this.boardId}/synced-links`, {
        method: "POST",
        json: { key: clip.key, sourceBoardId: clip.sourceBoardId },
      }).catch(() => {});
  }

  get preventOverlap(): boolean {
    return this.handle.types.meta.get("preventOverlap") !== false;
  }
  setPreventOverlap(v: boolean) {
    this.doc.transact(() => this.handle.types.meta.set("preventOverlap", v), LOCAL);
  }

  /** Create a note pre-filled with plain text (one paragraph per line). */
  async createNoteWithText(x: number, y: number, text: string): Promise<string> {
    const id = await this.create("note", x, y);
    const c = this.cards.get(id);
    const frag = c?.get("body");
    if (frag instanceof Y.XmlFragment) {
      this.doc.transact(() => {
        const paras = text.split(/\r?\n/).map((line) => {
          const p = new Y.XmlElement("paragraph");
          if (line) p.insert(0, [new Y.XmlText(line)]);
          return p;
        });
        frag.insert(0, paras);
      }, LOCAL);
    }
    useBoardUI.getState().setEditing(null);
    return id;
  }

  /** Fill a link card from the server's unfurl result. Safe to call repeatedly. */
  async resolveLink(cardId: string, url: string) {
    const c = this.cards.get(cardId);
    if (!c) return;
    this.doc.transact(() => {
      c.set("url", url);
      c.set("pending", true);
    }, LOCAL);
    try {
      const p = await api<{
        url: string;
        title: string | null;
        description: string | null;
        siteName: string | null;
        imageHash: string | null;
        faviconUrl: string | null;
        embedProvider: string | null;
        embedUrl: string | null;
        status: string;
      }>("/api/unfurl", { method: "POST", json: { url } });
      this.doc.transact(() => {
        const cc = this.cards.get(cardId);
        if (!cc) return;
        cc.set("url", p.url);
        cc.set("title", p.title ?? "");
        cc.set("description", p.description ?? "");
        cc.set("siteName", p.siteName ?? "");
        cc.set("imageHash", p.imageHash);
        cc.set("faviconUrl", p.faviconUrl);
        cc.set("embedProvider", p.embedProvider);
        cc.set("embedUrl", p.embedUrl);
        cc.set("pending", false);
        if (p.embedProvider === "youtube" || p.embedProvider === "vimeo" || p.embedProvider === "loom")
          cc.set("w", 360);
      }, "system");
    } catch {
      this.doc.transact(() => this.cards.get(cardId)?.set("pending", false), "system");
    }
  }

  selectedIds(): string[] {
    return [...useBoardUI.getState().selection].filter((id) => this.cards.has(id));
  }

  /** Connect selected cards in selection order with lines (Ctrl+K). */
  connect(ids = this.selectedIds()) {
    if (ids.length < 2) return;
    for (let i = 0; i < ids.length - 1; i++)
      createLine(this.doc, { cardId: ids[i]!, x: 0, y: 0 }, { cardId: ids[i + 1]!, x: 0, y: 0 }, this.userId);
    this.handle.undo.stopCapturing();
  }

  deleteLine(id: string) {
    this.doc.transact(() => this.handle.types.lines.delete(id), LOCAL);
    useBoardUI.setState({ selectedLine: null });
  }

  toggleLabel(labelId: string, ids = this.selectedIds()) {
    const all = ids.every((id) =>
      ((this.cards.get(id)?.get("labels") as string[] | undefined) ?? []).includes(labelId),
    );
    this.doc.transact(() => {
      for (const id of ids) {
        const c = this.cards.get(id);
        if (!c) continue;
        const cur = ((c.get("labels") as string[] | undefined) ?? []).filter((l) => l !== labelId);
        c.set("labels", all ? cur : [...cur, labelId]);
      }
    }, LOCAL);
  }

  toggleReaction(emoji: string, ids = this.selectedIds()) {
    this.doc.transact(() => {
      for (const id of ids) {
        const c = this.cards.get(id);
        if (!c) continue;
        let r = c.get("reactions") as Y.Map<unknown> | undefined;
        if (!(r instanceof Y.Map)) {
          r = new Y.Map();
          c.set("reactions", r);
        }
        const key = `${emoji}|${this.userId}`;
        if (r.has(key)) r.delete(key);
        else r.set(key, Date.now());
      }
    }, LOCAL);
  }

  /** Move cards into another board's Unsorted panel through the server, then remove them here. */
  async moveToBoard(targetBoardId: string, ids = this.selectedIds()) {
    const items = ids
      .map((id) => this.cards.get(id))
      .filter((c): c is Y.Map<unknown> => !!c && c.get("type") !== "board" && c.get("type") !== "column")
      .map((c) => ({ card: serializeCard(c) }));
    if (items.length === 0) return;
    await api(`/api/boards/${targetBoardId}/inbox`, { method: "POST", json: { items } });
    this.doc.transact(() => {
      for (const id of ids) if (this.cards.get(id)?.get("type") !== "board") this.cards.delete(id);
    }, LOCAL);
    pruneLines(this.doc);
    this.handle.undo.stopCapturing();
    useBoardUI.getState().clearSelection();
  }

  toUnsorted(ids = this.selectedIds()) {
    this.doc.transact(() => {
      for (const id of ids) {
        const c = this.cards.get(id);
        if (!c || c.get("type") === "board") continue;
        c.set("unsorted", true);
        c.set("parent", null);
        c.set("order", null);
      }
    }, LOCAL);
    pruneLines(this.doc);
    useBoardUI.getState().clearSelection();
  }

  /** Restore a trashed card from its server snapshot into the live doc. */
  async restoreFromTrash(trashId: string) {
    const r = await api<{ cardId: string; card: Parameters<typeof deserializeCard>[1] }>(
      `/api/trash/${trashId}/restore`,
      { method: "POST" },
    );
    const id = this.cards.has(r.cardId) ? undefined : r.cardId;
    const parent = r.card.fields.parent as string | null | undefined;
    const nid = deserializeCard(this.doc, r.card, {
      id,
      userId: this.userId,
      parent: parent && this.cards.has(parent) ? parent : null,
    });
    this.handle.undo.stopCapturing();
    useBoardUI.getState().select([nid]);
  }

  delete(ids = this.selectedIds()) {
    const line = useBoardUI.getState().selectedLine;
    if (line) return this.deleteLine(line);
    if (ids.length === 0) return;
    const boardsToTrash: string[] = [];
    const snapshots: {
      cardId: string;
      cardType: string;
      preview: string;
      card: ReturnType<typeof serializeCard>;
    }[] = [];
    // Children of a deleted column go to the trash with it.
    const expanded = new Set(ids);
    for (const id of ids)
      if (this.cards.get(id)?.get("type") === "column")
        this.cards.forEach((c, cid) => c.get("parent") === id && expanded.add(cid));
    this.doc.transact(() => {
      for (const id of expanded) {
        const c = this.cards.get(id);
        if (!c || c.get("locked")) continue;
        if (c.get("type") === "board" && c.get("childBoardId"))
          boardsToTrash.push(c.get("childBoardId") as string);
        else
          snapshots.push({
            cardId: id,
            cardType: c.get("type") as string,
            preview: cardPreview(c),
            card: serializeCard(c),
          });
        this.cards.delete(id);
      }
    }, LOCAL);
    this.handle.undo.stopCapturing();
    useBoardUI.getState().clearSelection();
    pruneLines(this.doc);
    if (snapshots.length)
      api(`/api/boards/${this.boardId}/trash`, { method: "POST", json: { items: snapshots } }).catch(
        () => {},
      );
    for (const b of boardsToTrash) api(`/api/boards/${b}`, { method: "DELETE" }).catch(() => {});
  }

  duplicate(ids = this.selectedIds()) {
    const created: string[] = [];
    this.doc.transact(() => {
      for (const id of ids) {
        const src = this.cards.get(id);
        if (!src) continue;
        const f = cardFields(src);
        if (f.type === "board" || f.type === "alias") continue; // boards are duplicated through the API later
        const body = src.get("body");
        const nid = createCard(this.doc, {
          type: f.type,
          x: f.x + 20,
          y: f.y + 20,
          w: f.w,
          h: f.h,
          userId: this.userId,
          fields: {
            ...f,
            id: undefined,
            x: undefined,
            y: undefined,
            z: undefined,
            createdAt: undefined,
            createdBy: undefined,
            labels: [...f.labels],
          },
          body: body instanceof Y.XmlFragment ? cloneFragment(body) : undefined,
        });
        created.push(nid);
      }
    }, LOCAL);
    this.handle.undo.stopCapturing();
    if (created.length) useBoardUI.getState().select(created);
  }

  setColor(color: string, mode?: "background" | "strip", ids = this.selectedIds()) {
    this.doc.transact(() => {
      for (const id of ids) {
        const c = this.cards.get(id);
        if (!c) continue;
        c.set("color", color);
        if (mode) c.set("colorMode", mode);
      }
    }, LOCAL);
    this.handle.undo.stopCapturing();
  }

  toggleLock(ids = this.selectedIds()) {
    this.doc.transact(() => {
      for (const id of ids) {
        const c = this.cards.get(id);
        if (c) c.set("locked", !c.get("locked"));
      }
    }, LOCAL);
  }

  front(ids = this.selectedIds()) {
    bringToFront(this.doc, ids);
  }
  back(ids = this.selectedIds()) {
    sendToBack(this.doc, ids);
  }

  nudge(dx: number, dy: number, ids = this.selectedIds()) {
    this.doc.transact(() => {
      for (const id of ids) {
        const c = this.cards.get(id);
        if (!c || c.get("locked")) continue;
        c.set("x", (c.get("x") as number) + dx);
        c.set("y", (c.get("y") as number) + dy);
      }
    }, LOCAL);
  }

  selectAll() {
    useBoardUI.getState().select([...this.cards.keys()]);
  }
  undo() {
    this.handle.undo.undo();
  }
  redo() {
    this.handle.undo.redo();
  }
}

function cloneFragment(src: Y.XmlFragment): Y.XmlFragment {
  const dst = new Y.XmlFragment();
  // Deep clone by serializing through Yjs' own clone of each child.
  const children = src.toArray().map((n) => n.clone());
  // Can't insert before integration; caller sets the fragment inside a transaction, so defer via a microtask-safe approach:
  // we return an empty fragment and copy after integration using observe-once.
  const copy = () => {
    if (dst.doc) dst.insert(0, children as Y.XmlElement[]);
  };
  queueMicrotask(copy);
  return dst;
}
